import React from 'react'
import styled from 'styled-components'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import loadable from '@loadable/component'

import VideoPlayerBlog from './shared/VideoPlayerBlog'
import { SiteLink } from './shared/SiteLink'
import { desktopVW, mobileVW } from '../styles'
import { useIsMobile } from '../hooks/useIsMobile'

const options = {
  renderMark: {
    [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    [MARKS.CODE]: (text) => <Sub>{text}</Sub>,
  },

  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { videoMp4, videoWebm, poster } = node?.data?.target ?? {}

      return (
        <VideoPlayerBlog
          webm={videoWebm?.file?.url}
          mp4={videoMp4?.file?.url}
          poster={poster?.file?.url}
        />
      )
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { title, description, file } = node?.data?.target ?? {}

      if (!node?.data?.target) return

      if (description) {
        return (
          <a
            href={description.includes('http') && description}
            title={!description.includes('http') && description}
            style={{ width: '100%' }}
          >
            <img
              alt={title}
              src={file.url + '?q=50&w=800'}
              style={{ width: '100%', margin: '0 auto' }}
            />
          </a>
        )
      } else {
        return (
          <img
            alt={title}
            src={file.url + '?q=50&w=800'}
            style={{ width: '100%', margin: '0 auto' }}
          />
        )
      }
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const { slug } = node?.data?.target ?? {}
      if (slug) {
        return <SiteLink to={slug}>{children}</SiteLink>
      } else return <p>{children}</p>
    },

    // embed YT / vimeo videos
    [INLINES.HYPERLINK]: (node) => {
      // THIS IS A BETTER WAY USING OUR REACT-PLAYER COMPONENT (IFRAMECONTAINER), BUT REACT-PLAYER HAS A DIV TAG WRAPPER IN P TAG
      // BEFORE WE FIND A WAY TO ELIMINATE THE DIV TAG, IT'S NOT SAFE TO USE
      // if (
      //   node.data.uri.includes('player.vimeo.com/video') ||
      //   node.data.uri.includes('vimeo.com/') ||
      //   node.data.uri.includes('youtube.com/') ||
      //   node.data.uri.includes('youtu.be/')
      // ) {
      //   return <IframeContainer url={node.data.uri} />

      // } else {
      //   return null
      // }

      // THEY MUST USE THE EMBED LINK FROM YT / VIMEO FOR THIS TO WORK
      if (node.data.uri.includes('player.vimeo.com/video')) {
        return (
          <IframeContainer>
            <iframe
              title='Unique Title 001'
              src={node.data.uri}
              frameBorder='0'
              allowFullScreen
            ></iframe>
          </IframeContainer>
        )
      } else if (node.data.uri.includes('youtube.com/embed')) {
        return (
          <IframeContainer>
            <iframe
              title='Unique Title 002'
              src={node.data.uri}
              allow='accelerometer; encrypted-media; gyroscope; picture-in-picture'
              frameBorder='0'
              allowFullScreen
            ></iframe>
          </IframeContainer>
        )
      } else if (node.data.uri.includes('typeform.com/')) {
        const Widget = loadable(async () => {
          const { Widget } = await import('react-typeform-embed')
          return Widget
        })
        const getTypeformId = () => {
          const { uri } = node.data
          const urlParts = uri.split('/')
          const lastPart = urlParts[urlParts.length - 1]
          return lastPart
        }
        const isMobile = useIsMobile()

        return (
          <Typeform>
            <Widget
              id={getTypeformId()}
              inlineOnMobile
              style={
                isMobile
                  ? { width: '80vw', height: '80vh' }
                  : { height: '33vw' }
              }
            />
          </Typeform>
        )
      } else {
        return (
          <a href={node.data.uri} target='_blank' style={{ color: '#22398b' }}>
            {node.content[0].value}
          </a>
        )
      }
    },
  },
  renderText: (text) => {
    return text.split('\n').reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
}

const RichText = ({ json }) => {
  return json?.raw ? renderRichText(json, options) : null
}

const Text = styled.p`
  margin-bottom: ${mobileVW(10)};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-bottom: ${desktopVW(10)};
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`

const Italic = styled.span`
  font-style: italic;
`

const Bold = styled.span`
  font-weight: bold;
`

const Sub = styled.sub`
  vertical-align: top;
  position: relative;
  top: -0.5em;
  font-size: small;
`

const IframeContainer = styled.span`
  padding-bottom: 56.25%;
  position: relative;
  display: block;
  width: 100%;

  > iframe {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const Typeform = styled.div`
  overflow: hidden;
  padding: ${(props) => props.theme.space.marginMobile}vw 0;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw 0;
  }
`

export default RichText
